// The pain entry model (PG-14, 2026-09-24). Its own module so both
// bodymap.ts (paint) and painRisk.ts (flag) can read it without importing
// each other.
//
// A painMap entry is a bare severity (both sides: every row written before
// 24/09, and Antenore's rule for them, a side-less row means BOTH sides) or
// `{ left?, right? }`. These functions are the ONLY place that knows it:
// every consumer reads through them, never the raw union, so the
// "bare = both" rule can't be re-invented (and drift) per call site.

import type { MuscleSlug, PainSeverity } from "./bodymap.js";
import type { PainEntry } from "./schemas/profile.js";
import type { PerSide } from "./side.js";

// The athlete's whole pain map: sparse {muscleSlug → PainEntry}. Lives here,
// not painRisk.ts, so this module's own docstring (both bodymap.ts and
// painRisk.ts read the pain entry model from here, without importing each
// other) is actually true: painRisk.ts imports PainMap from this file.
export type PainMap = Partial<Record<MuscleSlug, PainEntry>>;

export const PAIN_RANK: Readonly<Record<PainSeverity, number>> = {
  green: 0,
  yellow: 1,
  red: 2,
};

export const strongerPain = (a: PainSeverity, b: PainSeverity): PainSeverity =>
  PAIN_RANK[a] >= PAIN_RANK[b] ? a : b;

export function painSides(entry: PainEntry | undefined): PerSide<PainSeverity> {
  if (!entry) return {};
  if (typeof entry === "string") return { left: entry, right: entry };
  const out: PerSide<PainSeverity> = {};
  if (entry.left) out.left = entry.left;
  if (entry.right) out.right = entry.right;
  return out;
}

// Inverse of painSides, in the canonical spelling: equal sides collapse to
// the bare severity, no sides to `undefined` (the muscle leaves the map).
// One state, one spelling, so the API's deepEqual doesn't append a
// painHistory entry for a map that only changed shape.
export function compactPainSides(
  sides: PerSide<PainSeverity>,
): PainEntry | undefined {
  const { left, right } = sides;
  if (!left && !right) return undefined;
  if (left === right) return left;
  const out: { left?: PainSeverity; right?: PainSeverity } = {};
  if (left) out.left = left;
  if (right) out.right = right;
  return out;
}

// For consumers that are per muscle and not per side (the pain × volume
// overlay: training volume has no side). The worse of the two sides.
export function worstPainSeverity(
  entry: PainEntry | undefined,
): PainSeverity | undefined {
  let worst: PainSeverity | undefined;
  for (const sev of Object.values(painSides(entry))) {
    worst = worst ? strongerPain(worst, sev) : sev;
  }
  return worst;
}

// One state, one spelling, for a WHOLE map: run every entry through
// painSides() / compactPainSides() so a map mixing bare severities and
// `{ left?, right? }` entries that spell the SAME state collapses to one
// canonical shape. Callers that diff a stored map against an incoming one
// (the API's painHistory append) need this: without it, a semantically
// identical re-spelling reads as a change and appends a phantom
// painHistory entry (found by jl-test-owner, 2026-09-24).
export function compactPainMap(map: PainMap): PainMap {
  const out: PainMap = {};
  for (const [slug, entry] of Object.entries(map)) {
    const compacted = compactPainSides(painSides(entry));
    if (compacted !== undefined) {
      out[slug as MuscleSlug] = compacted;
    }
  }
  return out;
}

// Stable empty fallback so a `useMemo([..., painMap])` deps array keeps its
// identity across renders when the athlete has no pain entries (the common
// case): a fresh `{}` per render would cascade a new object into every
// panel memo. One frozen instance shared by every consumer instead of the
// three separate `{} as const` copies that used to live in
// apps/mobile/src/coach/useCoachProgressDeps.ts,
// apps/mobile/src/progress/progressDeps.ts and
// apps/web-rnw/src/progress/progressDeps.ts.
export const EMPTY_PAIN_MAP: PainMap = Object.freeze({});
